import { useEffect, useRef, useState } from 'react';
import { deriveMatchMinute } from '../../domain/clock';
import type { MatchRow } from '../../types';

/**
 * SES-4's display clock, and the wall clock every §5.6 nudge deadline is evaluated against.
 *
 * ── Why `requestAnimationFrame` and not an interval ─────────────────────────
 *
 * Design §2.4: *"The display clock is a `requestAnimationFrame`-driven read of `deriveMatchMinute`;
 * **correctness never depends on a timer firing** (SES-4). Nudge deadlines (LOCK-1, SES-7) are
 * absolute wall-clock values recomputed on every render and on resume, never `setTimeout` state."*
 *
 * Both halves of that matter on the target device and neither is stylistic:
 *
 * - **iOS throttles and suspends timers in a backgrounded page.** A `setInterval` clock drifts or
 *   stops in a pocket; `requestAnimationFrame` simply does not run while hidden and resumes on the
 *   first painted frame after the return, which is the moment the value is next *seen*. The clock is
 *   therefore correct whenever it is legible and costs nothing when it is not.
 * - **Nothing is derived from the tick.** Each frame re-reads `deriveMatchMinute` — the single
 *   temporal authority (INV-7/INV-10) — from the anchors on the row. If every frame were dropped the
 *   next one would still produce the right answer, which is what SES-4's *"best-effort"* means.
 *
 * ── One state update per minute, not per frame ──────────────────────────────
 *
 * The rendered value is whole minutes, so the frame callback compares the derived minute against the
 * last one and sets state only when it changes. A per-frame `setState` would reconcile the chrome
 * 120 times a second on the surface whose entire budget argument is about frames.
 *
 * The returned `now` is the wall clock **as read at that minute boundary**, which is exactly the
 * granularity SES-7's deadline needs: its threshold is a whole number of minutes after `kickoffTs`,
 * so the frame that crosses it is the frame that changes the minute.
 */
export interface CaptureClock {
  /** SES-4/SES-10's derived match minute. Never null, never negative (INV-10). */
  readonly minute: number;
  /** Wall clock, refreshed whenever the minute changes. The input to every nudge predicate. */
  readonly now: number;
}

export function useCaptureClock(match: MatchRow): CaptureClock {
  const [clock, setClock] = useState<CaptureClock>(() => {
    const now = Date.now();
    return { minute: deriveMatchMinute(match, match.state, now), now };
  });
  const minute = useRef(clock.minute);

  useEffect(() => {
    let frame = 0;

    // Re-seeded on every dependency change: a resumed or re-loaded match must not inherit the
    // previous row's minute as its comparison baseline, or the first change would be missed.
    const seed = Date.now();
    minute.current = deriveMatchMinute(match, match.state, seed);
    setClock({ minute: minute.current, now: seed });

    const tick = (): void => {
      const now = Date.now();
      const next = deriveMatchMinute(match, match.state, now);
      if (next !== minute.current) {
        minute.current = next;
        setClock({ minute: next, now });
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [match]);

  return clock;
}
